import { AllFieldTypeProps } from './field-types';
import { ButtonType } from '@/isomorphic/components/typography/button/button';
import { useForm } from '@/hooks/use-form';
import { useGtm } from '@/hooks/use-gtm';
import { GenericEvent, useDataLayer } from '@/hooks/use-data-layer';
import { FormRenderer } from './shared/form-renderer';
import { FormSettings } from './shared/types';

export type AllContactMeFormsProps = ContactFormSettings & AllFieldTypeProps;

export type ContactFormSettings = {
    generalRequiredErrorMessage?: string;
    generalSubmitErrorMessage?: string;
    generalSubmitButtonText?: string;
    bankAutoFillCustomer?: boolean;
    autoFillFields?: {
        bankAutofillCustomerName: boolean;
        bankAutofillCustomerEmail: boolean;
        bankAutofillCustomerAddress: boolean;
        bankAutofillCustomerHomePhone: boolean;
        bankAutofillCustomerMobilePhone: boolean;
    };
    autoFillAccount?: {
        bankAutofillCustomerAccountEnable: boolean;
        bankAutofillCustomerAccountTypes: [];
        bankAutofillCustomerLoanType: 'All' | 'Private' | 'Ellos';
    };
    formId?: string;
    permalink?: string;
    resetLink?: object;
    gtmOptions?: {
        gtmPaymentInsurance: boolean;
        gtmPaymentInsuranceType: 'Consumerloan' | 'Ellos';
    };
    gtmProduct?: string;
    gtmProductVariant?: string;
    gtmSendFormEvent?: boolean;
    gtmEventUseCustomProduct?: boolean;
    successTitle?: string;
    successMessage?: string;
    darkMode?: boolean;
    buttonType?: ButtonType;
};

export const ContactForm = (props: AllContactMeFormsProps) => {
    const { parsePropsToFieldObjects } = useForm();
    const { sendFormSubmitEvent } = useGtm();
    const { dataLayerPush } = useDataLayer();

    const parsedFields = parsePropsToFieldObjects(props);

    const formSettings: FormSettings = {
        formId: props.formId,
        successTitle: props.successTitle,
        successMessage: props.successMessage,
        generalSubmitErrorMessage: props.generalSubmitErrorMessage,
        darkMode: props?.darkMode || false,
        buttonType: props?.buttonType || 'primary',
        gtmProduct: props?.gtmProduct || '',
        gtmProductVariant: props?.gtmProductVariant || '',
        gtmSendFormEvent: props?.gtmSendFormEvent,
        gtmEventUseCustomProduct: props?.gtmEventUseCustomProduct,
        generalRequiredErrorMessage: props.generalRequiredErrorMessage,
    };

    const handleSuccess = () => {
        // Legacy GTM event
        sendFormSubmitEvent();

        if (!formSettings.gtmSendFormEvent) {
            return;
        }

        const event: GenericEvent = {
            event: 'FormSubmit',
        };

        if (formSettings.gtmEventUseCustomProduct && formSettings.gtmProduct) {
            event.Product = formSettings.gtmProduct;
            if (formSettings.gtmProductVariant) {
                event.ProductVariant = formSettings.gtmProductVariant;
            }

            dataLayerPush(event);
            return;
        }

        dataLayerPush(event, { usePageProductData: true });
    };

    return (
        <FormRenderer
            parsedFields={parsedFields}
            formSettings={formSettings}
            onSuccess={handleSuccess}
            dataComponent="resurs/contact-form"
            submissionRoute="/api/form"
        />
    );
};
